/**
 * Thin fetch wrapper.
 *
 * - Sends the access token from the auth store
 * - Adds X-Workspace-Id from the active workspace
 * - On 401, coordinates `/api/v1/auth/refresh` and retries with a bounded loop
 */
import { useAuth, type AuthSession } from '../stores/auth';

const BASE = '/api/v1';

interface Options extends Omit<RequestInit, 'body'> {
  json?: unknown;
  workspaceId?: string | null;
  auth?: boolean;
}

class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`HTTP ${status}`);
  }
}

type RefreshResult =
  | { ok: true; accessToken: string }
  | { ok: false };

const AUTH_REFRESH_LOCK = 'vodvault.auth.refresh';
let refreshInFlight: Promise<RefreshResult> | null = null;

/** Return a render-safe message for both application and FastAPI errors. */
function apiErrorMessage(error: ApiError, fallback = `HTTP ${error.status}`): string {
  const payload = error.payload;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (!payload || typeof payload !== 'object') return fallback;

  const body = payload as {
    error?: { message?: unknown };
    detail?: unknown;
  };
  if (typeof body.error?.message === 'string' && body.error.message.trim()) {
    return body.error.message;
  }
  if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;

  // FastAPI validation failures use an array of structured issues. Rendering
  // that array directly makes React throw (objects are not valid children),
  // so retain only their human-readable messages.
  if (Array.isArray(body.detail)) {
    const messages = body.detail.flatMap((issue) => {
      if (!issue || typeof issue !== 'object') return [];
      const message = (issue as { msg?: unknown }).msg;
      return typeof message === 'string' && message.trim() ? [message] : [];
    });
    if (messages.length > 0) return messages.join('; ');
  }
  return fallback;
}

function isAuthSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object') return false;
  const session = value as Partial<AuthSession>;
  return (
    typeof session.access_token === 'string' &&
    session.access_token.length > 0 &&
    !!session.user &&
    typeof session.user === 'object' &&
    Array.isArray(session.workspaces)
  );
}

function newerAccessToken(staleToken: string | null): string | null {
  const current = useAuth.getState().accessToken;
  return current && current !== staleToken ? current : null;
}

async function rehydrateNewerAccessToken(
  staleToken: string | null,
): Promise<string | null> {
  // Another tab may already have rotated the cookie and persisted its access
  // token. Pull that value in before attempting another one-time refresh.
  await useAuth.persist.rehydrate();
  return newerAccessToken(staleToken);
}

async function rotateRefreshToken(): Promise<RefreshResult> {
  const r = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
  if (!r.ok) {
    // Only an explicit authentication rejection invalidates the session.
    // Proxy outages and rate limits must leave the cookie and saved state
    // available for recovery when the host/network returns.
    if ([400, 401, 403].includes(r.status)) return { ok: false };
    throw new ApiError(r.status, { detail: 'Session service temporarily unavailable. Please retry.' });
  }
  const data: unknown = await r.json();
  if (!isAuthSession(data)) {
    throw new ApiError(502, { detail: 'Invalid session response. Please retry.' });
  }
  useAuth.getState().setLogin(data);
  return { ok: true, accessToken: data.access_token };
}

async function coordinatedRefresh(staleToken: string | null): Promise<RefreshResult> {
  const alreadyRotated = newerAccessToken(staleToken);
  if (alreadyRotated) return { ok: true, accessToken: alreadyRotated };

  const run = async (): Promise<RefreshResult> => {
    const peerToken = await rehydrateNewerAccessToken(staleToken);
    if (peerToken) return { ok: true, accessToken: peerToken };
    const result = await rotateRefreshToken();
    if (result.ok) return result;

    // Best-effort fallback for browsers without Web Locks: a peer tab's
    // successful Set-Cookie/localStorage update may land just after our
    // duplicate refresh was rejected.
    const latePeerToken = await rehydrateNewerAccessToken(staleToken);
    return latePeerToken
      ? { ok: true, accessToken: latePeerToken }
      : { ok: false };
  };

  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(AUTH_REFRESH_LOCK, run);
  }
  return run();
}

async function refresh(staleToken: string | null): Promise<RefreshResult> {
  const alreadyRotated = newerAccessToken(staleToken);
  if (alreadyRotated) return { ok: true, accessToken: alreadyRotated };

  // One expired page can release several React Query requests together.
  // Refresh tokens rotate on use, so those requests must share exactly one
  // refresh operation or the losing calls can be mistaken for token replay.
  if (!refreshInFlight) {
    refreshInFlight = coordinatedRefresh(staleToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Restore an in-memory/browser session from the HttpOnly refresh cookie. */
export async function restoreSession(): Promise<boolean> {
  if (useAuth.getState().accessToken) return true;
  const result = await refresh(null);
  return result.ok;
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const { json, workspaceId, auth = true, headers, ...rest } = opts;
  const state = useAuth.getState();
  let attemptedAccessToken = auth ? state.accessToken : null;

  const hdrs: Record<string, string> = {
    ...(headers as Record<string, string> | undefined),
  };
  if (json !== undefined) hdrs['content-type'] = 'application/json';
  if (auth && attemptedAccessToken) {
    hdrs['Authorization'] = `Bearer ${attemptedAccessToken}`;
  }
  const ws = workspaceId ?? state.currentWorkspace?.workspace_id;
  if (ws) hdrs['X-Workspace-Id'] = ws;

  const fire = async () =>
    fetch(`${BASE}${path}`, {
      ...rest,
      credentials: 'include',
      headers: hdrs,
      body: json !== undefined ? JSON.stringify(json) : undefined,
    });

  let response = await fire();

  // A late 401 may have been produced with the token that another request
  // already refreshed. At most two bounded recovery passes let us first use
  // that newer token, then perform one actual rotation if it too is expired.
  let recoveryPasses = 0;
  while (response.status === 401 && auth && recoveryPasses < 2) {
    const result = await refresh(attemptedAccessToken);
    if (!result.ok) {
      // Refresh failed (no cookie / revoked / expired). Clear stale state
      // and kick the user to the login page instead of looping on 401.
      useAuth.setState({
        accessToken: null,
        user: null,
        workspaces: [],
        currentWorkspace: null,
      });
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      break;
    }

    attemptedAccessToken = result.accessToken;
    hdrs['Authorization'] = `Bearer ${result.accessToken}`;
    response = await fire();
    recoveryPasses += 1;
  }

  if (!response.ok) {
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      payload = await response.text();
    }
    throw new ApiError(response.status, payload);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export { ApiError, apiErrorMessage };
